/**
 * 统一的HTTP请求工具
 * 使用XMLHttpRequest实现，兼容性更好
 */

// 默认配置
const DEFAULT_CONFIG = {
  timeout: 10000, // 10秒超时
  headers: {
    'Content-Type': 'application/json'
  }
};





/**
 * 对象合并函数 (兼容性考虑)
 */
function assign(target, ...sources) {
  if (Object.assign) {
    return Object.assign(target, ...sources);
  }
  // 兼容不支持Object.assign的浏览器
  const to = Object(target);
  for (let index = 0; index < sources.length; index++) {
    const nextSource = sources[index];
    if (nextSource != null) {
      for (const nextKey in nextSource) {
        if (Object.prototype.hasOwnProperty.call(nextSource, nextKey)) {
          to[nextKey] = nextSource[nextKey];
        }
      }
    }
  }
  return to;
}

/**
 * 将对象转换为查询字符串
 */
function objectToQueryString(obj) {
  if (URLSearchParams && typeof URLSearchParams === 'function') {
    const params = new URLSearchParams();
    for (const key in obj) {
      if (obj.hasOwnProperty(key) && obj[key] !== null && obj[key] !== undefined) {
        params.append(key, obj[key]);
      }
    }
    return params.toString();
  }
  
  // 兼容不支持URLSearchParams的浏览器
  const parts = [];
  for (const key in obj) {
    if (obj.hasOwnProperty(key) && obj[key] !== null && obj[key] !== undefined) {
      parts.push(encodeURIComponent(key) + '=' + encodeURIComponent(obj[key]));
    }
  }
  return parts.join('&');
}

/**
 * HTTP请求方法
 * @param {string} url - 请求URL
 * @param {Object} options - 请求配置
 * @param {string} options.method - 请求方法 (GET, POST, PUT, DELETE等)
 * @param {Object} options.data - 请求数据
 * @param {Object} options.headers - 请求头
 * @param {number} options.timeout - 超时时间(毫秒)
 * @param {boolean} options.withCredentials - 是否发送凭证
 * @returns {Promise} 返回Promise对象
 */
function request(url, options = {}) {
  return new Promise((resolve, reject) => {
    // 检查XMLHttpRequest支持
    if (!window.XMLHttpRequest) {
      reject(new Error('当前浏览器不支持XMLHttpRequest'));
      return;
    }

    const {
      method = 'GET',
      data = null,
      headers = {},
      timeout = DEFAULT_CONFIG.timeout,
      withCredentials = false,
    } = options;

    const xhr = new XMLHttpRequest();

    // 处理请求URL
    let requestUrl = url;
    
    // 在生产环境中去除 /api 前缀
    if (process.env.NODE_ENV === 'production' && requestUrl.startsWith('/api')) {
      requestUrl = requestUrl.substring(4); // 移除 '/api' 前缀
    }
    
    let requestData = null;

    if (method.toUpperCase() === 'GET' && data) {
      // GET请求将data转换为查询参数
      const queryString = objectToQueryString(data);
      if (queryString) {
        requestUrl += (requestUrl.includes('?') ? '&' : '?') + queryString;
      }
    } else if (data) {
      // 非GET请求处理数据
      if (typeof data === 'object') {
        requestData = JSON.stringify(data);
      } else {
        requestData = data;
      }
    }

    // 打开请求
    xhr.open(method.toUpperCase(), requestUrl, true);

    // 设置超时
    if (timeout > 0) {
      xhr.timeout = timeout;
    }

    // 设置凭证
    if (withCredentials) {
      xhr.withCredentials = true;
    }

    // 设置请求头
    const finalHeaders = assign({}, DEFAULT_CONFIG.headers, headers);
    for (const key in finalHeaders) {
      if (finalHeaders.hasOwnProperty(key)) {
        xhr.setRequestHeader(key, finalHeaders[key]);
      }
    }

    // 响应处理
    xhr.onreadystatechange = function() {
      if (xhr.readyState === 4) {
        const response = {
          status: xhr.status,
          statusText: xhr.statusText,
          headers: parseResponseHeaders(xhr.getAllResponseHeaders()),
          data: null
        };

        // 尝试解析响应数据
        try {
          if (xhr.responseText) {
            // 尝试解析JSON
            response.data = JSON.parse(xhr.responseText);
          }
        } catch (e) {
          // 如果不是JSON，直接使用原始文本
          response.data = xhr.responseText;
        }

        // 开发环境日志
        if (process.env.NODE_ENV === 'development') {
          console.log(`[HTTP] ${method.toUpperCase()} ${requestUrl} -> ${xhr.status}`, response.data);
        }

        // 判断请求是否成功
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(response);
        } else {
          const error = new Error(`请求失败: ${xhr.status} ${xhr.statusText}`);
          error.response = response;
          reject(error);
        }
      }
    };

    // 网络错误处理
    xhr.onerror = function() {
      reject(new Error('网络连接失败'));
    };

    // 超时处理
    xhr.ontimeout = function() {
      reject(new Error(`请求超时 (${timeout}ms)`));
    };

    // 取消处理
    xhr.onabort = function() {
      reject(new Error('请求被取消'));
    };

    // 发送请求
    try {
      xhr.send(requestData);
    } catch (error) {
      reject(new Error('发送请求失败: ' + error.message));
    }
  });
}

/**
 * 解析响应头
 * @param {string} headerStr - 响应头字符串
 * @returns {Object} 解析后的响应头对象
 */
function parseResponseHeaders(headerStr) {
  const headers = {};
  if (!headerStr) return headers;

  const headerPairs = headerStr.split('\r\n');
  for (let i = 0; i < headerPairs.length; i++) {
    const headerPair = headerPairs[i];
    const index = headerPair.indexOf(': ');
    if (index > 0) {
      const key = headerPair.substring(0, index).toLowerCase();
      const value = headerPair.substring(index + 2);
      headers[key] = value;
    }
  }
  return headers;
}

// 便捷方法
const http = {
  get: (url, params, config = {}) => {
    return request(url, { ...config, method: 'GET', data: params });
  },

  post: (url, data, config = {}) => {
    return request(url, { ...config, method: 'POST', data });
  },

  put: (url, data, config = {}) => {
    return request(url, { ...config, method: 'PUT', data });
  },

  delete: (url, config = {}) => {
    return request(url, { ...config, method: 'DELETE' });
  },

  patch: (url, data, config = {}) => {
    return request(url, { ...config, method: 'PATCH', data });
  }
};

export { request, http };
export default http; 